// ============================================
// BulkCreateProductsModal
// Tạo sản phẩm hàng loạt (sheet 2 — mục #13, #21)
// ============================================
//
// POST /products/bulk/ — JSON, tối đa 100 dòng mỗi lượt. Body bọc trong key
// `products` và cả lô nằm trong MỘT transaction: một dòng hỏng là 400 và
// không sản phẩm nào được tạo.
//
// HAI REQUEST, KHÔNG PHẢI MỘT. `ProductCreateSerializer` không có field tồn
// kho nào, nên số lượng KHÔNG thể đi kèm lúc tạo — nó đi ở bước hai qua
// POST /inventory/bulk-adjust/, dùng id trả về từ bước một. Hệ quả cần nhớ:
// bước một thành công mà bước hai hỏng thì sản phẩm ĐÃ tồn tại với tồn 0, và
// không có đường lùi. Vì vậy modal ở lại và báo đúng những SKU cần nhập tay,
// thay vì đóng lại như thể mọi thứ đều xong.
//
// PHẠM VI: đây KHÔNG phải import file. File CSV/XLSX đi qua nút "Import từ
// file" bên cạnh (`ImportProductsModal`), có upsert theo sku và chạy thử. Màn
// này để nhập tay vài sản phẩm mới.
//
// LỊCH SỬ: bản đầu là một ô textarea dán dòng từ Excel. Bỏ ngày 08/09 vì
// không dùng được: người nhập không thấy ô nào là cột nào, gõ thiếu một dấu
// tab là cả dòng thành "thiếu tên", và không có chỗ nào gắn ảnh. Giờ mỗi sản
// phẩm là một khối ô nhập rõ ràng, có ảnh riêng, thêm/bớt bằng nút.
//
// Chỉ CỬA HÀNG là chọn chung, vì nó là kho nhận tồn chứ không phải thuộc tính
// của sản phẩm. Danh mục và NCC nằm trên TỪNG DÒNG — bản 08/09 để chúng dùng
// chung theo giả định "một đợt nhập cùng một danh mục", giả định đó sai.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { CreateProductRequest } from '@conai/shared-types';
import type { SelectOption } from '../../components/shared';
import { ImageUploadField } from '../../components/shared';
import { useI18n } from '../../i18n';
import { useBulkCreateProducts, useBulkAdjust } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { StockAdjustmentType, type BulkAdjustItem } from '@conai/shared-types';
import { SearchableSelect } from './SearchableSelect';
// Dùng chung bộ chuẩn hoá giá với form đơn lẻ — xem ghi chú ở `toStorePricePayload`.
import { normalizePrice } from './utils';

/** Trần của BE — vượt là 400. */
const MAX_ROWS = 100;

interface DraftRow {
  /** Khoá cục bộ để React giữ đúng ô khi thêm/xoá dòng giữa chừng. */
  key: string;
  sku: string;
  name: string;
  unit: string;
  price: string;
  barcode: string;
  /**
   * Tồn ban đầu. KHÔNG đi cùng payload tạo sản phẩm: `ProductCreateSerializer`
   * không có field tồn kho nào, mà DRF bỏ qua field lạ trong im lặng — gửi
   * kèm thì sản phẩm vẫn tạo thành công với tồn bằng 0 và không ai biết.
   * Số này đi ở bước hai, qua POST /inventory/bulk-adjust/.
   */
  quantity: string;
  /**
   * Danh mục và NCC nằm TRÊN TỪNG DÒNG, không chọn chung cho cả lô. Bản trước
   * chọn chung vì đoán "một đợt nhập thường cùng danh mục" — đoán sai: một lô
   * hàng về gồm đủ thứ, mà chọn chung thì hoặc phải chia thành nhiều lượt tạo,
   * hoặc gán bừa một danh mục rồi sửa lại từng sản phẩm sau.
   */
  categoryId: string;
  supplierId: string;
  imageUrls: string[];
  /**
   * Giá riêng theo cửa hàng, giống hệt khối "Store Prices" của form đơn lẻ.
   *
   * Nằm TRÊN TỪNG DÒNG chứ không chọn chung cho cả lô: mỗi sản phẩm có bảng
   * giá riêng của nó, đó mới là lý do tồn tại của tính năng này. Một lô hàng
   * nhập về gồm đủ thứ, mà chung một bảng giá thì hoặc phải chia thành nhiều
   * lượt tạo, hoặc tạo xong rồi mở từng sản phẩm ra sửa giá lại — đúng cái
   * việc mà màn tạo hàng loạt sinh ra để tránh.
   *
   * Khác `quantity` ở một điểm quan trọng: giá theo cửa hàng đi CÙNG payload
   * tạo sản phẩm (`ProductCreateSerializer` có `store_prices`), không phải
   * bước hai như tồn kho. Nên không có cảnh "sản phẩm đã tạo mà giá chưa vào".
   */
  storePrices: DraftStorePrice[];
}

/**
 * Một dòng giá theo cửa hàng trong lúc đang nhập.
 *
 * Giữ mọi field ở dạng CHUỖI đúng như ô nhập, kể cả ngày: `datetime-local` trả
 * chuỗi, và chuyển sang ISO ngay lúc gõ sẽ làm ô nhảy giá trị khi người dùng
 * mới gõ được nửa ngày tháng. Việc quy đổi để dồn về lúc submit.
 */
interface DraftStorePrice {
  key: string;
  store: string;
  price: string;
  valid_from: string;
  valid_to: string;
}

let storePriceSeq = 0;
function emptyStorePrice(): DraftStorePrice {
  storePriceSeq += 1;
  return { key: `sp-${storePriceSeq}`, store: '', price: '', valid_from: '', valid_to: '' };
}

let rowSeq = 0;
function emptyRow(): DraftRow {
  rowSeq += 1;
  return {
    key: `row-${rowSeq}`,
    sku: '',
    name: '',
    // Để rỗng, KHÔNG điền sẵn 'piece'. Đơn vị mặc định làm người nhập tưởng
    // mình đã chọn rồi nên bỏ qua ô này — hàng cân ký vẫn lọt vào hệ thống
    // dưới dạng 'piece'. Bắt buộc nhập tay, đúng như các ô khác.
    unit: '',
    price: '',
    barcode: '',
    quantity: '',
    categoryId: '',
    supplierId: '',
    imageUrls: [],
    storePrices: [],
  };
}

/** Dòng thiếu gì. `null` = hợp lệ. Bỏ qua dòng còn trống hoàn toàn. */
function rowError(row: DraftRow): string | null {
  if (!row.sku.trim()) return 'sku';
  if (!row.name.trim()) return 'name';
  if (!row.unit.trim()) return 'unit';
  if (!row.price.trim() || Number.isNaN(Number(row.price))) return 'price';
  // Số lượng KHÔNG bắt buộc — tạo sản phẩm trước, nhập kho sau là chuyện
  // thường. Nhưng đã gõ thì phải là số nguyên không âm: `/inventory/adjust/`
  // chỉ nhận số nguyên, hàng cân ký chưa đi được đường này.
  if (row.quantity.trim()) {
    const q = Number(row.quantity);
    if (!Number.isInteger(q) || q < 0) return 'quantity';
  }
  // Giá theo cửa hàng: dòng nào đã động vào thì phải đủ CẢ cửa hàng lẫn giá.
  // Gửi nửa vời lên là 400 cho cả lô — mà cả lô nằm trong một transaction, nên
  // một ô giá bỏ trống sẽ kéo đổ luôn 99 sản phẩm còn lại.
  for (const sp of row.storePrices) {
    const touched = sp.store || sp.price.trim() || sp.valid_from || sp.valid_to;
    if (!touched) continue;
    if (!sp.store) return 'storePriceStore';
    const price = Number(sp.price);
    if (!sp.price.trim() || !Number.isFinite(price) || price < 0) return 'storePricePrice';
    // Khoảng hiệu lực ngược là lỗi im lặng kinh điển: BE nhận, nhưng giá đó
    // không bao giờ có hiệu lực và không ai hiểu vì sao.
    if (sp.valid_from && sp.valid_to && new Date(sp.valid_from) > new Date(sp.valid_to)) {
      return 'storePriceRange';
    }
  }
  // Hai dòng giá cùng một cửa hàng thì không biết cái nào thắng — chặn ở đây,
  // đừng để BE tự chọn hộ.
  const stores = row.storePrices.map((sp) => sp.store).filter(Boolean);
  if (new Set(stores).size !== stores.length) return 'storePriceDuplicate';
  return null;
}

/**
 * Dòng giá đã nhập -> payload `store_prices` của BE.
 *
 * Bỏ các khối còn trống hoàn toàn (người dùng bấm "thêm" rồi đổi ý), và chỉ
 * kèm `valid_from`/`valid_to` khi thật sự có — gửi chuỗi rỗng vào field ngày
 * là 400, còn bỏ hẳn key thì BE hiểu là "hiệu lực vô thời hạn", đúng ý.
 */
function toStorePricePayload(row: DraftRow) {
  return row.storePrices
    .filter((sp) => sp.store && sp.price.trim())
    .map((sp) => ({
      store: sp.store,
      price: normalizePrice(sp.price),
      ...(sp.valid_from ? { valid_from: new Date(sp.valid_from).toISOString() } : {}),
      ...(sp.valid_to ? { valid_to: new Date(sp.valid_to).toISOString() } : {}),
    }));
}

function isBlankRow(row: DraftRow): boolean {
  return (
    !row.sku.trim() &&
    !row.name.trim() &&
    // `unit` phải nằm trong danh sách này. Trước đây nó luôn có sẵn 'piece' nên
    // xét cũng vô nghĩa; giờ ô rỗng, mà bỏ sót thì dòng chỉ mới điền mỗi đơn vị
    // sẽ bị coi là trống và biến mất lúc submit, không một lời báo.
    !row.unit.trim() &&
    !row.price.trim() &&
    !row.barcode.trim() &&
    !row.quantity.trim() &&
    !row.categoryId &&
    !row.supplierId &&
    row.imageUrls.length === 0 &&
    // Dòng chỉ mới thêm một khối giá theo cửa hàng vẫn là dòng ĐANG NHẬP —
    // coi là trống thì nó biến mất lúc submit mà không một lời báo.
    row.storePrices.every(
      (sp) => !sp.store && !sp.price.trim() && !sp.valid_from && !sp.valid_to
    )
  );
}

function Field({
  label,
  required = false,
  error = false,
  children,
}: {
  label: string;
  required?: boolean;
  error?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label
        className={`mb-1 block text-[11px] font-semibold uppercase tracking-wider ${
          error ? 'text-red-600' : 'text-slate-500'
        }`}
      >
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

interface BulkCreateProductsModalProps {
  categoryOptions: SelectOption[];
  supplierOptions: SelectOption[];
  storeOptions: SelectOption[];
  onClose: () => void;
  onCreated?: () => void;
}

export function BulkCreateProductsModal({
  categoryOptions,
  supplierOptions,
  storeOptions,
  onClose,
  onCreated,
}: BulkCreateProductsModalProps) {
  const { t } = useI18n();
  const { selectedStoreId } = useStoreSelectionStore();
  const { mutate: bulkCreate, isPending } = useBulkCreateProducts();
  const { mutateAsync: bulkAdjust, isPending: isAdjusting } = useBulkAdjust();
  /**
   * Hai request nối nhau. Chỉ khoá theo `isPending` thì trong lúc đang nhập
   * tồn (bước hai) nút vẫn bấm được — và bấm là tạo lại nguyên lô sản phẩm
   * lần nữa, lần này SKU trùng nên 400, còn tồn thì đã ghi rồi.
   */
  const busy = isPending || isAdjusting;

  const [rows, setRows] = useState<DraftRow[]>(() => [emptyRow()]);
  /**
   * Tồn phải thuộc về MỘT cửa hàng cụ thể — kho là của cửa hàng, không của hệ
   * thống. Mặc định là cửa hàng đang chọn trên thanh đầu trang, vì danh sách
   * sản phẩm cũng lọc theo đúng cửa hàng đó (`store_id` trong `apiFilters`).
   * Để hai chỗ lệch nhau thì tạo xong sản phẩm sẽ KHÔNG hiện trong danh sách
   * và trông y như là không lưu được.
   */
  const [storeId, setStoreId] = useState(selectedStoreId ?? '');
  const [error, setError] = useState<string | null>(null);
  /** Chỉ tô đỏ ô thiếu SAU khi người dùng bấm Tạo — đừng mắng lúc họ đang gõ. */
  const [showErrors, setShowErrors] = useState(false);

  const patchRow = useCallback((key: string, patch: Partial<DraftRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const addRow = useCallback(() => setRows((prev) => [...prev, emptyRow()]), []);

  // ── Giá theo cửa hàng (một bộ handler cho mọi dòng, khoá theo row.key) ──
  const addStorePrice = useCallback((rowKey: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey ? { ...r, storePrices: [...r.storePrices, emptyStorePrice()] } : r
      )
    );
  }, []);

  const patchStorePrice = useCallback(
    (rowKey: string, spKey: string, patch: Partial<DraftStorePrice>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === rowKey
            ? {
                ...r,
                storePrices: r.storePrices.map((sp) =>
                  sp.key === spKey ? { ...sp, ...patch } : sp
                ),
              }
            : r
        )
      );
    },
    []
  );

  /**
   * Xoá TOÀN BỘ khối giá theo cửa hàng của một dòng sản phẩm.
   *
   * Khác nút "Remove" của từng khối: nút kia bỏ một cửa hàng, nút này dọn sạch.
   * Cần vì thêm nhầm vài cửa hàng rồi bấm "Remove" từng cái một là việc lặp vô
   * nghĩa — và mỗi lần bấm khối lại dồn lên, dễ bấm nhầm sang khối còn muốn giữ.
   *
   * KHÔNG hỏi xác nhận: đây là dữ liệu nháp chưa gửi đi đâu, và nút chỉ hiện
   * khi thật sự có khối để xoá. Dựng hộp thoại cho một thao tác trên form nháp
   * là thêm một bước bấm cho mọi lần dùng để phòng một lần bấm nhầm.
   */
  const clearStorePrices = useCallback((rowKey: string) => {
    setRows((prev) =>
      prev.map((r) => (r.key === rowKey ? { ...r, storePrices: [] } : r))
    );
  }, []);

  const removeStorePrice = useCallback((rowKey: string, spKey: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.key === rowKey
          ? { ...r, storePrices: r.storePrices.filter((sp) => sp.key !== spKey) }
          : r
      )
    );
  }, []);

  const removeRow = useCallback((key: string) => {
    // Luôn còn ít nhất một dòng, nếu không màn hình thành trống trơn.
    setRows((prev) => (prev.length === 1 ? [emptyRow()] : prev.filter((r) => r.key !== key)));
  }, []);

  const filledRows = useMemo(() => rows.filter((r) => !isBlankRow(r)), [rows]);
  const readyRows = useMemo(() => filledRows.filter((r) => rowError(r) === null), [filledRows]);

  /** Dòng nào có gõ số lượng — quyết định cửa hàng có bắt buộc hay không. */
  const rowsWithQuantity = useMemo(
    () => readyRows.filter((r) => r.quantity.trim() && Number(r.quantity) > 0),
    [readyRows]
  );

  const handleSubmit = () => {
    setError(null);
    setShowErrors(true);

    if (filledRows.length === 0) {
      setError(t('bulkProductsErrorNoRows'));
      return;
    }
    if (readyRows.length !== filledRows.length) {
      setError(t('bulkProductsErrorInvalidRows'));
      return;
    }
    if (filledRows.length > MAX_ROWS) {
      setError(t('bulkProductsErrorTooMany').replace('{max}', String(MAX_ROWS)));
      return;
    }
    // Chặn TRƯỚC khi tạo sản phẩm. Nếu để lọt, sản phẩm tạo xong rồi mới phát
    // hiện không biết nhập kho vào đâu — mà tạo thì không rollback lại được,
    // người dùng phải tự đi xoá từng cái.
    if (rowsWithQuantity.length > 0 && !storeId) {
      setError(t('bulkProductsErrorStoreRequired'));
      return;
    }

    const products: CreateProductRequest[] = readyRows.map((r) => {
      const storePrices = toStorePricePayload(r);
      return {
        sku: r.sku.trim(),
        name: r.name.trim(),
        unit: r.unit.trim(),
        price: r.price.trim(),
        // Quy ước payload TẠO MỚI của repo: không có giá trị thì BỎ HẲN key để BE
        // dùng mặc định, chứ không gửi null hay chuỗi rỗng.
        ...(r.barcode.trim() ? { barcode: r.barcode.trim() } : {}),
        ...(r.imageUrls.length > 0 ? { image_urls: r.imageUrls } : {}),
        ...(r.categoryId ? { category: r.categoryId } : {}),
        ...(r.supplierId ? { supplier: r.supplierId } : {}),
        // Giá theo cửa hàng đi NGAY trong payload tạo — `ProductCreateSerializer`
        // có `store_prices`, khác hẳn số lượng tồn (phải qua bulk-adjust ở bước
        // hai). Dùng `normalizePrice` và quy ước ISO giống hệt form đơn lẻ
        // (`toCreatePayload` trong ./utils) để hai đường tạo không sinh ra hai
        // định dạng giá khác nhau trong cùng một bảng.
        ...(storePrices.length > 0 ? { store_prices: storePrices } : {}),
      };
    });

    bulkCreate(products, {
      onSuccess: async (created) => {
        // BƯỚC HAI — nhập tồn ban đầu. Tách riêng vì `ProductCreateSerializer`
        // không có field tồn kho: số lượng phải đi qua /inventory/bulk-adjust/.
        if (rowsWithQuantity.length === 0) {
          onCreated?.();
          onClose();
          return;
        }

        // Ghép id trả về với dòng đã nhập THEO SKU, không theo thứ tự mảng.
        const idBySku = new Map((created ?? []).map((p) => [p.sku, p.id]));
        const stamp = Date.now();
        const adjustments: BulkAdjustItem[] = [];
        const missing: string[] = [];

        rowsWithQuantity.forEach((r) => {
          const sku = r.sku.trim();
          const productId = idBySku.get(sku);
          if (!productId) {
            missing.push(sku);
            return;
          }
          adjustments.push({
            product_id: productId,
            store_id: storeId,
            type: StockAdjustmentType.INCREASE,
            quantity: Number(r.quantity),
            // `reason` là chuỗi tự do (model đọc về khai `reason: string`),
            // không phải enum — đừng nhét mã kiểu 'RESTOCK' vào cho giống code.
            reason: t('bulkProductsInitialStockNote'),
            // Key thật cho từng dòng — bulk không nhận header Idempotency-Key,
            // và key rỗng từng làm BE trừ kho hai lần.
            idempotency_key: `bulk-create-${stamp}-${sku}`,
          });
        });

        try {
          const res = await bulkAdjust(adjustments);
          const failed = res.summary?.failed ?? res.failed?.length ?? 0;

          // Sản phẩm ĐÃ tạo xong rồi — không đóng modal như thể mọi thứ đều ổn.
          // Người dùng cần biết đúng những SKU nào còn tồn 0 để tự nhập lại.
          if (failed > 0 || missing.length > 0) {
            setError(
              t('bulkProductsStockPartial')
                .replace('{failed}', String(failed + missing.length))
                .replace('{total}', String(rowsWithQuantity.length))
            );
            onCreated?.();
            return;
          }
        } catch {
          setError(t('bulkProductsStockFailed'));
          onCreated?.();
          return;
        }

        onCreated?.();
        onClose();
      },
      onError: (err: unknown) => {
        setError(err instanceof Error ? err.message : t('failedToCreateProduct'));
      },
    });
  };

  const inputCls = (invalid: boolean) =>
    `h-9 w-full rounded-lg border px-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-200 ${
      invalid ? 'border-red-300 bg-red-50' : 'border-slate-300 focus:border-primary-400'
    }`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-[900px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('bulkProductsTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{t('bulkProductsSubtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {/* Chỉ CỬA HÀNG còn dùng chung. Nó không phải thuộc tính của sản
              phẩm — sản phẩm dùng chung toàn hệ thống — mà là kho nhận số tồn
              bên dưới, và một lượt nhập thì về đúng một kho. Danh mục với NCC
              đã chuyển xuống từng dòng. */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {t('store')}
              {rowsWithQuantity.length > 0 && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            <SearchableSelect
              options={storeOptions}
              value={storeId}
              onChange={setStoreId}
              placeholder={t('selectStore')}
            />
            <p className="mt-1 text-xs text-slate-400">{t('bulkProductsStoreHint')}</p>
            {/* Cảnh báo khi kho nhận hàng KHÁC cửa hàng đang xem. Hợp lệ, nhưng
                danh sách sản phẩm lọc theo cửa hàng đang xem nên tạo xong sẽ
                không thấy gì — và trông hệt như lưu hỏng. */}
            {storeId && selectedStoreId && storeId !== selectedStoreId && (
              <p className="mt-1 text-xs font-semibold text-amber-600">
                {t('bulkProductsStoreMismatch')}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {rows.map((row, index) => {
              const err = showErrors && !isBlankRow(row) ? rowError(row) : null;
              return (
                <div
                  key={row.key}
                  className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      {t('bulkProductsRowLabel').replace('{n}', String(index + 1))}
                    </span>
                    <button
                      onClick={() => removeRow(row.key)}
                      className="rounded px-2 py-1 text-xs font-semibold text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      {t('delete')}
                    </button>
                  </div>

                  <div className="flex gap-3">
                    {/* Ảnh đứng riêng bên trái: nó là ô duy nhất không gõ được,
                        trộn vào hàng input làm nhịp nhập liệu bị gãy.

                        Bản `compact` xếp DỌC (nút nằm dưới ô ảnh) nên chỉ cần
                        ~112px. Bản mặc định xếp ngang, rộng ~190px, từng làm
                        cột nút tràn đè lên các ô nhập bên phải. */}
                    <div className="w-28 shrink-0">
                      <ImageUploadField
                        variant="compact"
                        value={row.imageUrls}
                        onChange={(next) => patchRow(row.key, { imageUrls: next })}
                        entityType="product"
                        disabled={busy}
                      />
                    </div>

                    {/* MỌI ô đều có nhãn riêng. Bản trước chỉ dùng placeholder,
                        mà ô `unit` lại điền sẵn 'piece' — placeholder biến mất
                        ngay khi có giá trị, nên người dùng thấy chữ "piece" đen
                        trong một ô không tên và không biết đó là cái gì. */}
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Field label="SKU" required error={err === 'sku'}>
                          <input
                            value={row.sku}
                            onChange={(e) => patchRow(row.key, { sku: e.target.value })}
                            placeholder={t('enterProductCode')}
                            className={inputCls(err === 'sku')}
                          />
                        </Field>
                        <Field label={t('productName')} required error={err === 'name'}>
                          <input
                            value={row.name}
                            onChange={(e) => patchRow(row.key, { name: e.target.value })}
                            placeholder={t('enterProductName')}
                            className={inputCls(err === 'name')}
                          />
                        </Field>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-4">
                        <Field label={t('unitLabel')} required error={err === 'unit'}>
                          <input
                            value={row.unit}
                            onChange={(e) => patchRow(row.key, { unit: e.target.value })}
                            placeholder={t('unitPlaceholder')}
                            className={inputCls(err === 'unit')}
                          />
                        </Field>
                        <Field label={t('price')} required error={err === 'price'}>
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={row.price}
                            onChange={(e) => patchRow(row.key, { price: e.target.value })}
                            placeholder="0.00"
                            className={inputCls(err === 'price')}
                          />
                        </Field>
                        {/* `step=1` chứ không phải 0.01: đường /inventory/adjust/
                            chỉ nhận SỐ NGUYÊN. Để người dùng gõ 1.5 rồi mới báo
                            lỗi lúc submit là bẫy. */}
                        <Field label={t('quantity')} error={err === 'quantity'}>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={row.quantity}
                            onChange={(e) => patchRow(row.key, { quantity: e.target.value })}
                            placeholder="0"
                            className={inputCls(err === 'quantity')}
                          />
                        </Field>
                        <Field label={t('barCode')}>
                          <input
                            value={row.barcode}
                            onChange={(e) => patchRow(row.key, { barcode: e.target.value })}
                            placeholder={t('enterBarCode')}
                            className={inputCls(false)}
                          />
                        </Field>
                      </div>

                      <div className="grid gap-2 sm:grid-cols-2">
                        <Field label={t('categories')}>
                          <SearchableSelect
                            options={categoryOptions}
                            value={row.categoryId}
                            onChange={(next) => patchRow(row.key, { categoryId: next })}
                            placeholder={t('bulkProductsRowOptional')}
                            disabled={busy}
                          />
                        </Field>
                        <Field label={t('supplier')}>
                          <SearchableSelect
                            options={supplierOptions}
                            value={row.supplierId}
                            onChange={(next) => patchRow(row.key, { supplierId: next })}
                            placeholder={t('bulkProductsRowOptional')}
                            disabled={busy}
                          />
                        </Field>
                      </div>

                      {/* ── Giá theo cửa hàng ──────────────────────────────
                          Mặc định GẤP LẠI. Phần lớn sản phẩm dùng một giá
                          chung, bày sẵn bốn ô nhập cho mỗi dòng sẽ làm màn tạo
                          hàng loạt dài gấp đôi vì một tính năng ít dùng. Đã có
                          giá riêng thì hiện luôn số lượng ngay trên nút, để
                          không ai phải mở từng dòng ra kiểm. */}
                      <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            {t('storePrices')}
                            {row.storePrices.length > 0 && (
                              <span className="ml-1.5 rounded-full bg-primary-50 px-1.5 py-0.5 text-[10px] font-bold text-primary-700">
                                {row.storePrices.length}
                              </span>
                            )}
                          </span>
                          <div className="flex items-center gap-1.5">
                            {/* Chỉ hiện khi CÓ khối để xoá — một nút xoá luôn
                                đứng đó cạnh nút thêm, kể cả lúc chưa có gì, chỉ
                                làm người dùng phải đọc để biết nó vô tác dụng. */}
                            {row.storePrices.length > 0 && (
                              <button
                                type="button"
                                onClick={() => clearStorePrices(row.key)}
                                disabled={busy}
                                className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                              >
                                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                  <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {t('clearStorePricesBtn')}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => addStorePrice(row.key)}
                              disabled={busy}
                              className="flex items-center gap-1 rounded-lg border border-primary-300 bg-primary-50 px-2 py-1 text-[11px] font-semibold text-primary-700 transition-colors hover:bg-primary-100 disabled:opacity-40"
                            >
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                              {t('addStorePriceBtn')}
                            </button>
                          </div>
                        </div>

                        {row.storePrices.length === 0 ? (
                          <p className="mt-1 text-[11px] text-slate-400">
                            {t('bulkProductsStorePriceHint')}
                          </p>
                        ) : (
                          <div className="mt-2 space-y-2">
                            {row.storePrices.map((sp, spIndex) => (
                              <div
                                key={sp.key}
                                className="rounded-lg border border-slate-200 bg-slate-50 p-2"
                              >
                                <div className="mb-1.5 flex items-center justify-between">
                                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                    #{spIndex + 1}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeStorePrice(row.key, sp.key)}
                                    disabled={busy}
                                    className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                  >
                                    {t('remove')}
                                  </button>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  <Field
                                    label={t('productsStore')}
                                    required
                                    error={err === 'storePriceStore' || err === 'storePriceDuplicate'}
                                  >
                                    <SearchableSelect
                                      options={storeOptions}
                                      value={sp.store}
                                      onChange={(next) =>
                                        patchStorePrice(row.key, sp.key, { store: next })
                                      }
                                      placeholder={t('selectStore')}
                                      disabled={busy}
                                    />
                                  </Field>
                                  <Field
                                    label={t('price')}
                                    required
                                    error={err === 'storePricePrice'}
                                  >
                                    <input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={sp.price}
                                      onChange={(e) =>
                                        patchStorePrice(row.key, sp.key, { price: e.target.value })
                                      }
                                      placeholder="0.00"
                                      className={inputCls(err === 'storePricePrice')}
                                    />
                                  </Field>
                                  {/* Hai ô ngày TUỲ CHỌN — bỏ trống nghĩa là
                                      giá có hiệu lực ngay và vô thời hạn, đó là
                                      ca thường gặp nhất. */}
                                  <Field label={t('validFrom')} error={err === 'storePriceRange'}>
                                    <input
                                      type="datetime-local"
                                      value={sp.valid_from}
                                      onChange={(e) =>
                                        patchStorePrice(row.key, sp.key, {
                                          valid_from: e.target.value,
                                        })
                                      }
                                      className={inputCls(err === 'storePriceRange')}
                                    />
                                  </Field>
                                  <Field label={t('validTo')} error={err === 'storePriceRange'}>
                                    <input
                                      type="datetime-local"
                                      value={sp.valid_to}
                                      onChange={(e) =>
                                        patchStorePrice(row.key, sp.key, {
                                          valid_to: e.target.value,
                                        })
                                      }
                                      className={inputCls(err === 'storePriceRange')}
                                    />
                                  </Field>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {err && (
                        <p className="text-xs font-semibold text-red-600">
                          {/* Số lượng KHÔNG bắt buộc — nó chỉ hỏng khi gõ sai
                              kiểu. Dùng chung câu "thiếu {field}" ở đây sẽ bảo
                              người dùng điền một ô mà họ vừa điền rồi. */}
                          {err === 'quantity'
                            ? t('bulkProductsInvalidQuantity')
                            : err === 'storePriceStore'
                              ? t('bulkProductsStorePriceMissingStore')
                              : err === 'storePricePrice'
                                ? t('bulkProductsStorePriceMissingPrice')
                                : err === 'storePriceRange'
                                  ? t('bulkProductsStorePriceBadRange')
                                  : err === 'storePriceDuplicate'
                                    ? t('bulkProductsStorePriceDuplicate')
                                    : t('bulkProductsMissingField').replace('{field}', err)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={addRow}
            disabled={rows.length >= MAX_ROWS}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-600 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:opacity-40"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t('bulkProductsAddRow')}
          </button>

          {error && (
            <div className="whitespace-pre-line rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-6 py-4">
          <span className="text-xs text-slate-500">
            {t('bulkProductsReadyCount')
              .replace('{valid}', String(readyRows.length))
              .replace('{total}', String(filledRows.length))}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={busy || readyRows.length === 0}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? t('saving') : t('bulkProductsSubmit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
